// Mirror of backend helper/clubs/constants.js — keep in sync.
// Single source of truth for court/club domain enums on the frontend, so
// filters, forms, and labels never drift from what the backend accepts.
// Source: zoco-back-tickets-find-event/helper/clubs/constants.js

// Bookable sports. Football has one entry per player-count variant.
export const SPORTS = [
  "padel",
  "football5",
  "football6",
  "football7",
  "football8",
  "football9",
  "football10",
  "football11",
  "tennis",
];

// Court surface materials.
export const SURFACES = [
  "cesped_natural",
  "cesped_sintetico",
  "cemento",
  "muro",
  "sintetico_y_blindex",
  "sintetico_y_muro",
  "polvo_de_ladrillo",
  "court_flex",
];

// Court roofing/enclosure status.
export const ENCLOSURES = ["cubierta", "descubierta", "semicubierta"];

// Club-level amenities/services (distinct from base Bar `amenities`).
export const SERVICES = [
  "wifi",
  "vestuario",
  "gimnasio",
  "estacionamiento",
  "parrillas",
  "ayuda_medica",
  "bar_restaurante",
  "torneos",
  "cumpleanos",
  "escuelita_deportiva",
  "colegios",
  "quincho",
  "disposicion_de_paletas",
];

// Club-level bookable durations (minutes).
export const ALLOWED_DURATIONS = [60, 90, 120];

// Spanish display labels, keyed by the backend enum values above.
export const SPORT_LABELS = {
  padel: "Pádel",
  football5: "Fútbol 5",
  football6: "Fútbol 6",
  football7: "Fútbol 7",
  football8: "Fútbol 8",
  football9: "Fútbol 9",
  football10: "Fútbol 10",
  football11: "Fútbol 11",
  tennis: "Tenis",
};

export const SURFACE_LABELS = {
  cesped_natural: "Césped natural",
  cesped_sintetico: "Césped sintético",
  cemento: "Cemento",
  muro: "Muro",
  sintetico_y_blindex: "Sintético y blindex",
  sintetico_y_muro: "Sintético y muro",
  polvo_de_ladrillo: "Polvo de ladrillo",
  court_flex: "Court flex",
};

export const ENCLOSURE_LABELS = {
  cubierta: "Cubierta",
  descubierta: "Descubierta",
  semicubierta: "Semicubierta",
};

export const SERVICE_LABELS = {
  wifi: "Wifi",
  vestuario: "Vestuario",
  gimnasio: "Gimnasio",
  estacionamiento: "Estacionamiento",
  parrillas: "Parrillas",
  ayuda_medica: "Ayuda médica",
  bar_restaurante: "Bar / restaurante",
  torneos: "Torneos",
  cumpleanos: "Cumpleaños",
  escuelita_deportiva: "Escuelita deportiva",
  colegios: "Colegios",
  quincho: "Quincho",
  disposicion_de_paletas: "Disposición de paletas",
};

export const DURATION_LABELS = {
  60: "60 min",
  90: "90 min",
  120: "120 min",
};

// Default number of players per booked slot, keyed by sport.
// Mirror of backend helper/clubs/constants.js DEFAULT_PLAYERS_BY_SPORT — the
// backend applies the same default when the checkout omits `partySize`, so the
// number the buyer sees pre-filled is the number that gets stored.
export const DEFAULT_PLAYERS_BY_SPORT = {
  padel: 4,
  tennis: 2,
  football5: 10,
  football6: 12,
  football7: 14,
  football8: 16,
  football9: 18,
  football10: 20,
  football11: 22,
};

// Fallback for a court whose sport is empty or unknown.
export const DEFAULT_PLAYERS_FALLBACK = 2;

// Upper bound accepted by the backend for a buyer-supplied player count.
export const MAX_PLAYERS_PER_RESERVATION = 50;

/**
 * @param {string} sport - a SPORTS value (or anything, including "").
 * @returns {number} the default player count for that sport.
 */
export function resolveDefaultPlayers(sport) {
  return DEFAULT_PLAYERS_BY_SPORT[sport] || DEFAULT_PLAYERS_FALLBACK;
}

/**
 * Per-player share of a court price. Returns null when either input is missing
 * or non-positive — callers must render nothing rather than a "$0 cada uno",
 * which would read as a real (and free) price.
 *
 * @param {number|null|undefined} totalPrice
 * @param {number|null|undefined} players
 * @returns {number|null} the rounded per-player amount, or null.
 */
export function perPlayerPrice(totalPrice, players) {
  const total = Number(totalPrice);
  const count = Number(players);
  if (!Number.isFinite(total) || total <= 0) return null;
  if (!Number.isInteger(count) || count < 1) return null;
  return Math.round(total / count);
}
